import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  HelpCircle, 
  ChevronRight,
  MessageCircle
} from 'lucide-react';

const faqItems = [
  {
    question: 'Is HR Nest for companies or employers?',
    answer: 'No. HR Nest is designed exclusively to support individuals.'
  },
  {
    question: 'Does HR Nest provide legal advice?',
    answer: 'No. All content is educational. Users should consult licensed professionals for legal, financial, or medical advice.'
  },
  {
    question: 'Who can join HR Nest?',
    answer: 'Anyone seeking career guidance, professional growth, or personal development support.'
  },
  {
    question: 'Is my information confidential?',
    answer: 'Yes. We do not sell data and take privacy seriously.'
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes. Memberships can be canceled at any time with no refund.'
  },
  {
    question: 'Is HR Nest a nonprofit?',
    answer: 'No. HR Nest operates as a for-profit educational platform.'
  },
];

export const FAQ = () => {
  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-teal-light/20 relative" data-testid="faq-hero">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-teal/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-purple/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-hr-teal/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-6 animate-bounce-subtle">
              <HelpCircle className="h-4 w-4 text-hr-teal animate-pulse" />
              <span className="text-hr-teal text-sm font-medium">Got Questions?</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              Frequently Asked <span className="text-gradient bg-gradient-to-r from-hr-teal to-hr-purple bg-clip-text text-transparent animate-gradient">Questions</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Find answers to common questions about HR Nest and how we can help you on your career journey.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white relative overflow-hidden" data-testid="faq-content">
        <div className="absolute top-1/2 left-0 w-72 h-72 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        <div className="absolute top-1/2 right-0 w-72 h-72 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
        
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border border-stone-200 rounded-2xl px-6 hover:border-hr-teal/50 hover:shadow-lg transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading font-medium text-hr-text-primary hover:text-hr-purple py-6 transition-colors duration-300">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-hr-text-secondary pb-6">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* Contact CTA */}
          <div className="text-center mt-16 animate-fade-in-up" style={{ opacity: 0, animationDelay: '0.6s' }}>
            <div className="inline-flex flex-col items-center gap-4 p-8 rounded-3xl bg-gradient-to-r from-hr-purple-light to-hr-teal-light">
              <div className="w-16 h-16 rounded-full bg-white/80 flex items-center justify-center">
                <MessageCircle className="h-8 w-8 text-hr-purple" />
              </div>
              <h3 className="font-heading text-2xl font-semibold text-hr-text-primary">
                Still have questions?
              </h3>
              <p className="text-hr-text-secondary max-w-md">
                We're here to help! Reach out to us and we'll get back to you as soon as possible.
              </p>
              <Link to="/contact" data-testid="faq-contact-btn">
                <Button className="group bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105">
                  Contact Us
                  <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
